/**
 * Metadata rico para session_started → attribution_events + Slack real-time.
 * Paridad con buildCheckoutEmailCaptureContext (device, geo, CTA, dual variant).
 * Precios: solo USD (sin ARS / MercadoPago).
 */

import { detectTrafficSource } from "@/lib/detectTrafficSource";
import { getHeroCtaSlackExtra } from "@/lib/heroCtaSlackContext";
import { getSlackFunnelDiagnosticsExtraPostHog } from "@/lib/posthogAnalytics";
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";

function stripEmpty(
  obj: Record<string, string | number | boolean | undefined>,
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null || v === "") continue;
    if (v === false) continue;
    out[k] = v;
  }
  return out;
}

export function buildSessionStartedMetadata(): Record<string, string | number | boolean> {
  if (typeof window === "undefined") return {};

  const tc = getServerTemplateConfig();
  const traffic = detectTrafficSource();
  const hero = getHeroCtaSlackExtra();
  const diagnostics = getSlackFunnelDiagnosticsExtraPostHog({ replayNearNow: true });
  const dv = tc?.dualVariant;

  const referrerNow =
    typeof document !== "undefined" && document.referrer
      ? document.referrer.slice(0, 300)
      : undefined;
  const screen =
    typeof window !== "undefined" && window.screen
      ? `${window.screen.width}x${window.screen.height}`
      : undefined;
  const ua =
    typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 300) : undefined;

  const priceUsd =
    typeof dv?.priceUsd === "number" && dv.priceUsd > 0 ? dv.priceUsd : undefined;

  return stripEmpty({
    ...diagnostics,
    city: tc?.city?.trim(),
    province: tc?.province?.trim(),
    us_state: tc?.usState?.trim(),
    neighborhood: tc?.neighborhood?.trim(),
    neighborhood_city: tc?.neighborhoodCity?.trim(),
    geo_source: tc?.geoSource,
    template_source: tc?.templateSource,
    geo_summary: hero.geo_summary,
    dual_variant_id: dv?.id,
    dual_variant_display_name: dv?.displayName,
    dual_variant_assignment_scope: dv?.assignmentScope,
    dual_variant_assignment_geo: dv?.assignmentGeo,
    dual_variant_price_usd: priceUsd,
    dual_variant_stripe_price_id: dv?.stripePriceId,
    dual_variant_vip_label: dv?.vipLabel,
    dual_variant_vip_subtitle: dv?.vipSubtitle,
    membership_currency: priceUsd != null ? "USD" : undefined,
    ab_bucket: tc?.abBucket,
    hero_cta_type: tc?.ctaType ?? hero.hero_cta_type,
    hero_cta_label: hero.hero_cta_label ?? tc?.heroCta?.label ?? tc?.ctaLabel,
    hero_cta_url: hero.hero_cta_url,
    hero_cta_emoji: hero.hero_cta_emoji,
    hero_cta_resolution_source:
      tc?.ctaResolutionSource ?? hero.hero_cta_resolution_source,
    hero_admin_type: tc?.heroCta?.type,
    worker_template: hero.worker_template ?? tc?.templateName,
    vpn_detected: tc?.vpnDetected,
    as_organization: tc?.asOrganization,
    global_hide_hero_cta: tc?.globalHideHeroCta,
    timezone: tc?.timezone?.trim(),
    postal_code: tc?.postalCode?.trim(),
    traffic_source: traffic.source,
    traffic_medium: traffic.medium,
    traffic_channel: traffic.channel,
    in_app_browser: traffic.inAppBrowser || undefined,
    referrer_now: referrerNow,
    screen,
    user_agent: ua,
    page_url: window.location.href.slice(0, 500),
    landing_path: window.location.pathname,
  });
}
